import React from 'react';
import { Modal, Row, Col, Typography, Divider, Tag, Space, Tooltip, message } from 'antd';
import AllocationModalDropdown from '../AllocationModalDropdown';
import AllocationModalDropdownCheckbox from '../AllocationModalDropdownCheckbox';
import { getAccounts } from '../../utils/accountStructure/accountStructureUtils';
import Localization from '../../utils/localization';
import './styles.scss';

const { Title } = Typography;

const ALL = -2;

const toNum = (v) => {
  if (v == null || v === '' || v === '0') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toNumArr = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map(Number)
    .filter(Number.isFinite);

const flattenEntities = (list = []) => {
  const out = [];
  const walk = (nodes = []) => {
    nodes.forEach((n) => {
      out.push(n);
      if (Array.isArray(n.children) && n.children.length) {
        walk(n.children);
      }
    });
  };
  walk(list);
  return out;
};

const normalizeScenarioSelection = (sel, allowedScenarioIds) => {
  const nums = toNumArr(sel);

  if (nums.includes(ALL)) return [ALL];

  const allowed = new Set(allowedScenarioIds.map(Number));
  return Array.from(new Set(nums.filter((id) => allowed.has(id))));
};

const canonicalizeAllocation = (a) => ({
  ...a,
  businessCaseId: toNum(a.businessCaseId) ?? 0,
  entityId: toNum(a.entityId),
  scenarioId: Array.isArray(a.scenarioId) ? a.scenarioId.map(Number) : [],
  accountId: toNum(a.accountId) ?? 0,
  targetEntityId: toNum(a.targetEntityId),
  targetScenarioId: Array.isArray(a.targetScenarioId) ? a.targetScenarioId.map(Number) : [],
  targetAccountId: toNum(a.targetAccountId) ?? 0,
});

const AllocationModal = ({
  businessCases,
  entities,
  accountStructure,
  allocation,
  setAllocation,
  isAllocationModalOpen,
  setIsAllocationModalOpen,
  handleCreateAllocation,
  handleEditAllocation,
  edit,
  setEdit,
  originalEntities,
}) => {
  const allEntitiesFlat = React.useMemo(() => flattenEntities(entities || []), [entities]);
  const originalEntityNum = toNum(originalEntities?.entityId);
  const originalTargetEntityNum = toNum(originalEntities?.targetEntityId);

  const allScenarios = React.useMemo(
    () =>
      allEntitiesFlat.flatMap((e) => (Array.isArray(e.scenario) ? e.scenario : [])),
    [allEntitiesFlat]
  );

  const scenarioById = React.useMemo(
    () => new Map(allScenarios.map((s) => [Number(s.scenario_id), s])),
    [allScenarios]
  );

  const scenarioNameById = React.useMemo(
    () => new Map(allScenarios.map((s) => [String(s.scenario_id), s.name])),
    [allScenarios]
  );

  const selectedBusinessCase = React.useMemo(
    () =>
      (businessCases || []).find(
        (bc) => String(bc.business_case_id) === String(allocation?.businessCaseId ?? '')
      ),
    [businessCases, allocation?.businessCaseId]
  );

  const hasBusinessCase = !!selectedBusinessCase;

  const businessCaseScenarioIds = React.useMemo(
    () =>
      new Set(
        (selectedBusinessCase?.business_scenario || []).map((bs) => Number(bs.scenario_id))
      ),
    [selectedBusinessCase]
  );

  const getAllowedScenarios = React.useCallback(
    (entityId) => {
      const entityNum = toNum(entityId);

      return allScenarios.filter((sc) => {
        const scId = Number(sc.scenario_id);
        const scEntityId = Number(sc.entity_id);

        if (hasBusinessCase && !businessCaseScenarioIds.has(scId)) return false;
        if (entityNum != null && scEntityId !== entityNum) return false;

        return true;
      });
    },
    [allScenarios, hasBusinessCase, businessCaseScenarioIds]
  );

  const filteredEntities = React.useMemo(() => {
    if (!hasBusinessCase) return allEntitiesFlat;

    const allowedEntityIds = new Set(
      allScenarios
        .filter((sc) => businessCaseScenarioIds.has(Number(sc.scenario_id)))
        .map((sc) => Number(sc.entity_id))
    );

    return allEntitiesFlat.filter((e) =>
      allowedEntityIds.has(Number(e.entity_id ?? e.id ?? e.entityId))
    );
  }, [hasBusinessCase, allEntitiesFlat, allScenarios, businessCaseScenarioIds]);

  const filteredScenarios = React.useMemo(
    () => getAllowedScenarios(allocation?.entityId),
    [getAllowedScenarios, allocation?.entityId]
  );

  const filteredTargetScenarios = React.useMemo(
    () => getAllowedScenarios(allocation?.targetEntityId),
    [getAllowedScenarios, allocation?.targetEntityId]
  );

  const allowedFromScenarioIds = React.useMemo(
    () => filteredScenarios.map((sc) => Number(sc.scenario_id)),
    [filteredScenarios]
  );

  const allowedTargetScenarioIds = React.useMemo(
    () => filteredTargetScenarios.map((sc) => Number(sc.scenario_id)),
    [filteredTargetScenarios]
  );

  const filteredEntityItems = React.useMemo(() => {
    const items = filteredEntities.map((e) => ({
      id: String(e.entity_id ?? e.id ?? e.entityId),
      name: e.name ?? String(e.entity_id ?? e.id ?? e.entityId),
    }));

    const present = new Set(items.map((i) => i.id));

    [originalEntityNum, originalTargetEntityNum].forEach((num) => {
      if (num == null || present.has(String(num))) return;
      const entity = allEntitiesFlat.find(
        (e) => Number(e.entity_id ?? e.id ?? e.entityId) === num
      );
      if (entity) {
        items.push({
          id: String(num),
          name: `${entity.name ?? num} (no scenarios here)`,
        });
        present.add(String(num));
      }
    });

    return items;
  }, [filteredEntities, allEntitiesFlat, originalEntityNum, originalTargetEntityNum]);

  const SelectedScenarioTags = ({ ids }) => {
    const nums = (ids || []).map(Number).filter(Number.isFinite);

    if (nums.includes(ALL)) {
      return (
        <Space size="small" wrap style={{ marginTop: 6 }}>
          <Tag>All</Tag>
        </Space>
      );
    }

    const MAX_TAGS = 4;
    const list = nums.map((id) => {
      const sid = String(id);
      return { id: sid, name: scenarioNameById.get(sid) || sid };
    });

    if (list.length === 0) return null;

    const head = list.slice(0, MAX_TAGS);
    const rest = list.length - head.length;

    return (
      <Space size="small" wrap style={{ marginTop: 6 }}>
        {head.map(({ id, name }) => (
          <Tooltip title={name} key={id}>
            <Tag>{name}</Tag>
          </Tooltip>
        ))}
        {rest > 0 && <Tag>+{rest} more</Tag>}
      </Space>
    );
  };



  // sanitize selected entities if BC changes and current entity is no longer valid
  React.useEffect(() => {
    const allowedEntityIds = new Set(
      filteredEntities.map((e) => Number(e.entity_id ?? e.id ?? e.entityId))
    );

    setAllocation((prev) => {
      if (!prev) return prev;

      const entityNum = toNum(prev.entityId);
      const targetEntityNum = toNum(prev.targetEntityId);

      let changed = false;
      const next = { ...prev };

      if (entityNum != null && !allowedEntityIds.has(entityNum) && entityNum !== originalEntityNum) {
        next.entityId = null;
        next.scenarioId = [];
        changed = true;
      }

      if (targetEntityNum != null && !allowedEntityIds.has(targetEntityNum) && targetEntityNum !== originalTargetEntityNum) {
        next.targetEntityId = null;
        next.targetScenarioId = [];
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [filteredEntities, setAllocation]);

  // sanitize FROM scenarios whenever allowed scope changes
  React.useEffect(() => {
    setAllocation((prev) => {
      if (!prev) return prev;
      const nextScenarioId = normalizeScenarioSelection(prev.scenarioId, allowedFromScenarioIds);
      const same = JSON.stringify(prev.scenarioId || []) === JSON.stringify(nextScenarioId);
      return same ? prev : { ...prev, scenarioId: nextScenarioId };
    });
  }, [allowedFromScenarioIds, setAllocation]);

  // sanitize TO scenarios whenever allowed scope changes
  React.useEffect(() => {
    setAllocation((prev) => {
      if (!prev) return prev;
      const nextTargetScenarioId = normalizeScenarioSelection(
        prev.targetScenarioId,
        allowedTargetScenarioIds
      );
      const same =
        JSON.stringify(prev.targetScenarioId || []) === JSON.stringify(nextTargetScenarioId);
      return same ? prev : { ...prev, targetScenarioId: nextTargetScenarioId };
    });
  }, [allowedTargetScenarioIds, setAllocation]);

  const handleOk = () => {
    const payload = canonicalizeAllocation(allocation);

    if (!payload.scenarioId.length || !payload.targetScenarioId.length) {
      message.warning('Select at least one scenario on both sides.');
      return;
  }
  
  if (edit) {
    if (handleEditAllocation(payload)) {
      setEdit(false);
      setIsAllocationModalOpen(false);
    }
  } else {
    if (handleCreateAllocation(payload)) {
      setIsAllocationModalOpen(false);
    }
  }
};

  const handleCancel = () => {
    const emptyAllocation = {
      businessCaseId: 0,
      entityId: null,
      scenarioId: [],
      accountId: 0,
      targetEntityId: null,
      targetScenarioId: [],
      targetAccountId: 0,
    };

    setEdit(false);
    setAllocation(emptyAllocation);
    setIsAllocationModalOpen(false);
  };

  const accountItems = React.useMemo(
  () =>
    getAccounts([], accountStructure).map((account) => ({
      id: String(account.associated_account_id ?? account.account_id),
      name: account.account_number ? `${account.account_number} — ${account.name}` : account.name,
    })),
  [accountStructure]
);

  return (
    <Modal
      bodyStyle={{
        minHeight: '45vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      title={edit ? Localization.allocations.editAllocation : Localization.allocations.addAllocation}
      visible={isAllocationModalOpen}
      confirmLoading={false}
      onCancel={handleCancel}
      onOk={handleOk}
      width={500}
    >
      <div>
        <Row>
          <Title level={4} style={{ marginTop: '10px' }}>
            {Localization.allocations.from}
          </Title>
        </Row>

        <Row>
          <Col span={12}>
            <Title level={5} style={{ marginTop: '10px' }}>
              {Localization.allocations.businessCase}
            </Title>
          </Col>
          <Col span={12}>
            <AllocationModalDropdown
              allocation={allocation}
              setAllocation={setAllocation}
              label={'Choose Business Case'}
              type={'businessCaseId'}
              edit={edit}
              items={(businessCases || []).map((businessCase) => ({
                id: String(businessCase.business_case_id),
                name: businessCase.name,
              }))}
            />
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <Title level={5} style={{ marginTop: '10px' }}>
              {Localization.allocations.entity}
            </Title>
          </Col>
          <Col span={12}>
            <AllocationModalDropdown
              allocation={allocation}
              setAllocation={setAllocation}
              label={'Choose Entity'}
              type={'entityId'}
              edit={edit}
              items={filteredEntityItems}
            />
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <Title level={5} style={{ marginTop: '10px' }}>
              {Localization.allocations.scenario}
            </Title>
          </Col>
          <Col span={12}>
            <AllocationModalDropdownCheckbox
              allocation={allocation}
              setAllocation={setAllocation}
              label={'Choose Scenario(s)'}
              type={'scenarioId'}
              checkedScenarios={allocation.scenarioId}
              edit={edit}
              items={filteredScenarios.map((sc) => ({
                id: String(sc.scenario_id),
                name: sc.name,
              }))}
              onConcreteScenarioPicked={(scenarioId) => {
                const sc = scenarioById.get(Number(scenarioId));
                if (!sc) return;

                setAllocation((prev) => {
                  if (toNum(prev.entityId) != null) return prev;
                  return {
                    ...prev,
                    entityId: String(sc.entity_id),
                  };
                });
              }}
            />
            <SelectedScenarioTags ids={allocation.scenarioId} />
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <Title level={5} style={{ marginTop: '10px' }}>
              {Localization.allocations.account}
            </Title>
          </Col>
          <Col span={12}>
            <AllocationModalDropdown
              key={`accountId-${allocation.accountId}-${JSON.stringify(allocation.scenarioId)}`}
              allocation={allocation}
              setAllocation={setAllocation}
              label={'Choose Account'}
              type={'accountId'}
              edit={edit}
              items={accountItems}
            />
          </Col>
        </Row>

        <Divider />

        <Row>
          <Title level={4}>{Localization.allocations.to}</Title>
        </Row>

        <Row>
          <Col span={12}>
            <Title level={5} style={{ marginTop: '10px' }}>
              {Localization.allocations.targetEntity}
            </Title>
          </Col>
          <Col span={12}>
            <AllocationModalDropdown
              allocation={allocation}
              setAllocation={setAllocation}
              label={'Choose Target Entity'}
              type={'targetEntityId'}
              edit={edit}
              items={filteredEntityItems}
            />
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <Title level={5} style={{ marginTop: '10px' }}>
              {Localization.allocations.targetScenario}
            </Title>
          </Col>
          <Col span={12}>
            <AllocationModalDropdownCheckbox
              allocation={allocation}
              setAllocation={setAllocation}
              label={'Choose Target Scenario(s)'}
              type={'targetScenarioId'}
              checkedScenarios={allocation.targetScenarioId}
              edit={edit}
              items={filteredTargetScenarios.map((sc) => ({
                id: String(sc.scenario_id),
                name: sc.name,
              }))}
              onConcreteScenarioPicked={(scenarioId) => {
                const sc = scenarioById.get(Number(scenarioId));
                if (!sc) return;

                setAllocation((prev) => {
                  if (toNum(prev.targetEntityId) != null) return prev;
                  return {
                    ...prev,
                    targetEntityId: String(sc.entity_id),
                  };
                });
              }}
            />
            <SelectedScenarioTags ids={allocation.targetScenarioId} />
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <Title level={5} style={{ marginTop: '10px' }}>
              {Localization.allocations.targetAccount}
            </Title>
          </Col>
          <Col span={12}>
            <AllocationModalDropdown
              key={`targetAccountId-${allocation.targetAccountId}-${JSON.stringify(
                allocation.targetScenarioId
              )}`}
              allocation={allocation}
              setAllocation={setAllocation}
              label={'Choose Target Account'}
              type={'targetAccountId'}
              edit={edit}
              items={accountItems}
            />
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default AllocationModal;